/**
 * Direct-zone dashed-IP labels (ARCHITECTURE §6.2), browser-safe.
 *
 * IPv4: dots → dashes ("192.168.1.50" → "192-168-1-50").
 * IPv6: RFC 5952 canonical compressed lowercase form with ':' → '-', so
 * "2001:db8::1" → "2001-db8--1". IPv4-mapped addresses unmap to IPv4.
 *
 * The Go implementation is gokit/directzone; both sides are pinned by
 * contracts/fixtures/dashedip.json.
 */

/** Strict IPv4 check: four 0–255 octets, no leading zeros. */
function parseIpv4(addr: string): string[] | null {
  const parts = addr.split(".");
  if (parts.length !== 4) return null;
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p)) return null;
    if (p.length > 1 && p.startsWith("0")) return null;
    if (Number(p) > 255) return null;
  }
  return parts;
}

/** RFC 5952 canonical form of an IPv6 address, or null if invalid. */
function canonicalIpv6(addr: string): string | null {
  if (addr.includes("%")) return null;
  let head = addr;

  // Trailing dotted-quad (e.g. ::ffff:1.2.3.4) → two hex groups.
  let quadGroups: string[] | null = null;
  const lastColon = head.lastIndexOf(":");
  if (head.includes(".") && lastColon !== -1) {
    const quad = parseIpv4(head.slice(lastColon + 1));
    if (!quad) return null;
    const [a, b, c, d] = quad.map(Number);
    quadGroups = [
      (((a! << 8) | b!) >>> 0).toString(16),
      (((c! << 8) | d!) >>> 0).toString(16),
    ];
    head = head.slice(0, lastColon + 1) + "\0"; // placeholder group
  }

  const doubles = head.split("::");
  if (doubles.length > 2) return null;
  const parseSide = (s: string): string[] | null => {
    if (s === "") return [];
    const groups = s.split(":");
    for (const g of groups) {
      if (g === "\0") continue;
      if (!/^[0-9a-fA-F]{1,4}$/.test(g)) return null;
    }
    return groups;
  };

  // The dotted quad is always the final token, so its placeholder sits at
  // the end of the right-hand side; it expands to TWO hex groups.
  const substituteQuad = (side: string[]): string[] | null => {
    if (!quadGroups) return side;
    if (side[side.length - 1] !== "\0") return null;
    return [...side.slice(0, -1), ...quadGroups];
  };

  let groups: string[];
  if (doubles.length === 2) {
    const left = parseSide(doubles[0]!);
    const rawRight = parseSide(doubles[1]!);
    if (!left || !rawRight) return null;
    const right = substituteQuad(rawRight);
    if (!right) return null;
    const fill = 8 - (left.length + right.length);
    if (fill < 1) return null;
    groups = [...left, ...Array<string>(fill).fill("0"), ...right];
  } else {
    const rawOnly = parseSide(head);
    if (!rawOnly) return null;
    const only = substituteQuad(rawOnly);
    if (!only || only.length !== 8) return null;
    groups = only;
  }
  if (groups.includes("\0")) return null;

  const norm = groups.map((g) => {
    const v = parseInt(g, 16);
    return Number.isNaN(v) ? null : v.toString(16);
  });
  if (norm.includes(null)) return null;
  const canon = norm as string[];

  // IPv4-mapped (::ffff:a.b.c.d) is the caller's job to unmap — signal it.
  if (
    canon.slice(0, 5).every((g) => g === "0") &&
    canon[5] === "ffff"
  ) {
    const hi = parseInt(canon[6]!, 16);
    const lo = parseInt(canon[7]!, 16);
    return `::ffff:${hi >> 8}.${hi & 255}.${lo >> 8}.${lo & 255}`;
  }

  // Longest run of zero groups (≥2, leftmost) compresses to "::".
  let bestStart = -1;
  let bestLen = 0;
  for (let i = 0; i < 8; i++) {
    if (canon[i] !== "0") continue;
    let j = i;
    while (j < 8 && canon[j] === "0") j++;
    if (j - i > bestLen) {
      bestLen = j - i;
      bestStart = i;
    }
    i = j;
  }
  if (bestLen < 2) return canon.join(":");
  const left = canon.slice(0, bestStart).join(":");
  const right = canon.slice(bestStart + bestLen).join(":");
  return `${left}::${right}`;
}

/**
 * Renders an IP address as its dashed direct-zone label.
 * Throws on anything that is not a plain unicast IP literal.
 */
export function dashIp(addr: string): string {
  const trimmed = addr.trim().toLowerCase();
  if (!trimmed.includes(":")) {
    if (!parseIpv4(trimmed)) throw new Error(`not an IP address: ${addr}`);
    return trimmed.replaceAll(".", "-");
  }
  const canon = canonicalIpv6(trimmed);
  if (canon === null) throw new Error(`not an IP address: ${addr}`);
  if (canon.startsWith("::ffff:") && canon.includes(".")) {
    // IPv4-mapped: unmap, exactly like Go's netip Unmap().
    return canon.slice("::ffff:".length).replaceAll(".", "-");
  }
  return canon.replaceAll(":", "-");
}

/** LAN/WAN direct candidate hostname for a server. */
export function directHostname(ip: string, serverHash: string, zone: string): string {
  return `${dashIp(ip)}.${serverHash}.${zone}`;
}

/**
 * Relay candidate hostname — one label under the hash (wildcard-covered).
 * nodeId is the full node id and must start with "relay-" (e.g. "relay-1"),
 * matching the direct-zone grammar and the relay_nodes registry key.
 */
export function relayHostname(nodeId: string, serverHash: string, zone: string): string {
  if (!nodeId.startsWith("relay-")) throw new Error(`relay node id must start with "relay-": ${nodeId}`);
  return `${nodeId}.${serverHash}.${zone}`;
}
